import type {
  PaigeCcCatalog,
  PaigeCcCatalogs,
  PaigeCcCheck,
  PaigeCcCreated,
  PaigeCcPlan,
  PaigeCcWaiting,
  PaigeN1Field,
  PaigeProductDestinationsBridge,
} from './product-destination-types';

/** One call on the job's destinations channel (`destination-channel.ts`); rejects with Paige's reason. */
export type DestinationCall = <T>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
) => Promise<T>;

/** `?a=1&b=2` of the values given, or nothing. */
function query(values: Record<string, string | undefined>): string {
  const search = new URLSearchParams(
    Object.entries(values).filter((pair): pair is [string, string] => Boolean(pair[1])),
  );
  return search.size > 0 ? `?${search.toString()}` : '';
}

/**
 * Content Central's and Nucleus One's calls on the job's channel (the API's ContentCentralEndpoints,
 * NucleusOneEndpoints and DestinationSendAgainEndpoints): every one made by Paige with the stored key.
 */
export function productDestinationChannel(call: DestinationCall): PaigeProductDestinationsBridge {
  return {
    ccCatalogs: (id, q, cursor) =>
      call<PaigeCcCatalogs>(`/${id}/content-central/catalogs${query({ q, cursor })}`, 'GET'),
    ccCatalog: (id, catalogId) =>
      call<PaigeCcCatalog>(
        `/${id}/content-central/catalogs/${encodeURIComponent(catalogId)}`,
        'GET',
      ),
    ccCheck: (id, matching) => call<PaigeCcCheck>(`/${id}/content-central/check`, 'POST', matching),
    ccWaiting: async (id) =>
      (await call<{ types: PaigeCcWaiting[] }>(`/${id}/content-central/waiting`, 'GET')).types,
    ccPlan: (id) => call<PaigeCcPlan>(`/${id}/content-central/catalog-plan`, 'POST'),
    ccCreate: (id, planId, planFingerprint) =>
      call<PaigeCcCreated>(`/${id}/content-central/catalog-plan/apply`, 'POST', {
        planId,
        planFingerprint,
      }),
    n1Fields: async (id, place) =>
      (
        await call<{ fields: PaigeN1Field[] }>(
          `/${id}/nucleus-one/fields${query({ place })}`,
          'GET',
        )
      ).fields,
    sendAgain: async (id) =>
      (await call<{ documents: number }>(`/${id}/send-again`, 'POST')).documents,
  };
}
